import { useState } from "react";
import { genresByType, regionOptions, ratingOptions, yearOptions } from "./filterOptions";

export interface CategoryFilterState {
  region: string;
  year: string;
  genre: string;
  minRating: number;
}

interface FilterBarProps {
  filter: CategoryFilterState;
  onChange: (next: Partial<CategoryFilterState>) => void;
  onReset: () => void;
  availableGenres: string[];
}

interface FilterGroupProps {
  label: string;
  options: { label: string; value: string | number }[];
  active: string | number;
  onSelect: (value: string | number) => void;
}

function FilterGroup({ label, options, active, onSelect }: FilterGroupProps) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200 py-3 last:border-b-0 lg:flex-row lg:items-start lg:gap-4">
      <span className="w-16 shrink-0 pt-1 text-xs font-medium text-foreground-400">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={`${label}-${String(option.value)}`}
            type="button"
            onClick={() => onSelect(option.value)}
            className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition-colors ${
              active === option.value
                ? "bg-primary-500 font-medium text-background-50"
                : "text-foreground-600 hover:bg-background-200 hover:text-primary-700"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({ filter, onChange, onReset, availableGenres }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const activeCount =
    (filter.region !== "全部地区" ? 1 : 0) +
    (filter.year !== "全部年份" ? 1 : 0) +
    (filter.genre !== "全部类型" ? 1 : 0) +
    (filter.minRating > 0 ? 1 : 0);

  const genreList = availableGenres.length > 0 ? availableGenres : genresByType;

  const groups = (
    <>
      <FilterGroup
        label="地区"
        options={regionOptions.map((item) => ({ label: item, value: item }))}
        active={filter.region}
        onSelect={(value) => onChange({ region: String(value) })}
      />
      <FilterGroup
        label="年份"
        options={yearOptions.map((item) => ({ label: item, value: item }))}
        active={filter.year}
        onSelect={(value) => onChange({ year: String(value) })}
      />
      <FilterGroup
        label="类型"
        options={genreList.map((item) => ({ label: item, value: item }))}
        active={filter.genre}
        onSelect={(value) => onChange({ genre: String(value) })}
      />
      <FilterGroup
        label="评分"
        options={ratingOptions.map((item) => ({ label: item.label, value: item.value }))}
        active={filter.minRating}
        onSelect={(value) => onChange({ minRating: Number(value) })}
      />
    </>
  );

  return (
    <>
      <div className="hidden rounded-lg border border-background-200 bg-background-50 px-4 py-1 lg:block">{groups}</div>

      <div className="flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 lg:hidden">
        <p className="text-xs text-foreground-500">
          共 <span className="font-semibold text-primary-700">{activeCount}</span> 项筛选条件生效
        </p>
        <div className="flex items-center gap-2">
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onReset}
              className="cursor-pointer whitespace-nowrap text-xs text-foreground-500"
            >
              重置
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50"
          >
            <i className="ri-filter-3-line" aria-hidden="true" />
            筛选
            {activeCount > 0 && (
              <span className="rounded-full bg-accent-500 px-1.5 text-[10px] font-bold text-primary-950">{activeCount}</span>
            )}
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true" aria-label="筛选面板">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setOpen(false)}
            className="absolute inset-0 h-full w-full cursor-pointer bg-primary-950/50"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[78vh] overflow-y-auto rounded-t-xl bg-background-50 p-4">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-heading text-base font-semibold text-foreground-950">筛选影片</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-background-100 text-foreground-600"
              >
                <i className="ri-close-line" aria-hidden="true" />
              </button>
            </div>
            {groups}
            <div className="mt-4 flex items-center gap-3 pb-2">
              <button type="button" onClick={onReset} className="btn-ghost flex-1">
                重置条件
              </button>
              <button type="button" onClick={() => setOpen(false)} className="btn-primary flex-1">
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}